/* eslint-env browser, webextensions */
import React, { useState, useEffect, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { Home, DollarSign, Tag, LogOut, LogIn, Menu, X, Moon, Sun, Chrome } from 'lucide-react';

import { auth } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { Button, IconButton } from './ui';

export default function Navbar({ navigate, currentPath, user, isAuthReady }) {
    const { theme, toggleTheme } = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const navLinks = [
        { name: 'Home', path: '/home', icon: Home },
        { name: 'Card Manager', path: '/tool', icon: DollarSign },
        { name: 'Deals', path: '/deals', icon: Tag },
        { name: 'Extension', path: '/extension', icon: Chrome },
    ];

    // Scroll-aware glassmorphism
    const handleScroll = useCallback(() => {
        setIsScrolled(window.scrollY > 10);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleLinkClick = (e, path) => {
        e.preventDefault();
        navigate(path);
        setIsMenuOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setIsMenuOpen(false);
            navigate('/home');
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const handleSignInClick = (e) => {
        e.preventDefault();
        if (currentPath !== '/login') {
            navigate('/login');
        }
        setIsMenuOpen(false);
    };

    // User avatar with initials
    const UserAvatar = ({ name, size = 'sm' }) => {
        const initials = name
            ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
            : '?';
        const sizeClasses = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
        return (
            <div className={`${sizeClasses} rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 font-bold flex items-center justify-center flex-shrink-0`}>
                {initials}
            </div>
        );
    };

    const isActive = (linkPath) => {
        if (linkPath === '/home') return currentPath === '/home' || currentPath === '/';
        return currentPath === linkPath;
    };

    return (
        <>
            <header className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'nav-solid border-b border-slate-200/60 dark:border-slate-700/60' : 'nav-transparent bg-white dark:bg-slate-800 border-b border-transparent'}`}>
                <nav className="container mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
                    <a href="/home" onClick={(e) => handleLinkClick(e, '/home')} className="flex items-center">
                        <img src="/images/logo_with_text_and_magnifying_glass.webp" alt="Gift Scout Logo" className="h-10 mr-2 sm:mr-3" />
                    </a>

                    {/* Desktop Nav */}
                    <div className="hidden md:flex items-center space-x-1">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.path}
                                onClick={(e) => handleLinkClick(e, link.path)}
                                aria-current={isActive(link.path) ? 'page' : undefined}
                                className={`relative flex items-center px-3 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${isActive(link.path)
                                    ? 'bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                <link.icon className="inline-block h-4 w-4 mr-1.5" />
                                {link.name}
                            </a>
                        ))}

                        <div className="ml-2 pl-2 border-l border-slate-200 dark:border-slate-700 flex items-center space-x-2">
                            <IconButton
                                onClick={toggleTheme}
                                icon={theme === 'dark' ? Sun : Moon}
                                label={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                            />

                            {!isAuthReady ? (
                                <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
                            ) : user ? (
                                <div className="flex items-center space-x-2">
                                    <UserAvatar name={user.displayName || user.email} />
                                    <span className="text-sm text-slate-600 dark:text-slate-300 font-medium hidden lg:block max-w-[120px] truncate" title={user.email}>
                                        {user.displayName?.split(' ')[0] || user.email.split('@')[0]}
                                    </span>
                                    <IconButton
                                        onClick={handleSignOut}
                                        icon={LogOut}
                                        label="Sign Out"
                                        variant="danger"
                                    />
                                </div>
                            ) : (
                                <Button onClick={handleSignInClick} icon={LogIn} size="sm" pill>Sign In</Button>
                            )}
                        </div>
                    </div>

                    {/* Mobile hamburger */}
                    <div className="md:hidden flex items-center space-x-2">
                        <IconButton
                            onClick={toggleTheme}
                            icon={theme === 'dark' ? Sun : Moon}
                            label={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                        />
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="text-slate-700 dark:text-slate-300 hover:text-brand-700 dark:hover:text-brand-400 p-1 rounded-lg transition-colors"
                            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
                        </button>
                    </div>
                </nav>

                {/* Mobile menu with slide animation */}
                {isMenuOpen && (
                    <div className="md:hidden mobile-menu-enter border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
                        <div className="container mx-auto px-4 py-4">
                            <div className="flex flex-col space-y-1">
                                {navLinks.map((link) => (
                                    <a
                                        key={link.name}
                                        href={link.path}
                                        onClick={(e) => handleLinkClick(e, link.path)}
                                        className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive(link.path)
                                            ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300'
                                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60'
                                            }`}
                                    >
                                        <link.icon className="inline-block h-5 w-5 mr-2.5" />
                                        {link.name}
                                    </a>
                                ))}
                            </div>

                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                {!isAuthReady ? (
                                    <div className="h-10 w-full bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse"></div>
                                ) : user ? (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <UserAvatar name={user.displayName || user.email} size="md" />
                                            <div>
                                                <p className="text-sm font-medium text-slate-800 dark:text-white">{user.displayName || 'User'}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">{user.email}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleSignOut}
                                            className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-sm font-medium"
                                        >
                                            <LogOut size={16} />
                                            <span>Sign Out</span>
                                        </button>
                                    </div>
                                ) : (
                                    <Button onClick={handleSignInClick} icon={LogIn} fullWidth>Sign In</Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
}